/**
 * Regra PURA do financeiro gerencial (Task 16, brief 3.8 / D-16.1). **Leia isto
 * antes de mexer — é o coração da task.**
 *
 * Endereço (ADR-0010, exceção ENCERRADA em R5/FEAT-006, D-R3R5-11): como a
 * máquina de pedido (`packages/crm`), a expedição (`packages/fulfillment`) e o
 * RMA (`packages/rma`), esta regra foi PROMOVIDA de
 * `apps/plataforma/src/financial/managerial.ts` para `packages/financial`
 * (handoff H-16.A fechado). O servidor (`apps/api-dev` hoje, Edge Function
 * depois) e o app IMPORTAM estas funções DAQUI — não há uma segunda
 * implementação no servidor. O arquivo antigo em
 * `apps/plataforma/src/financial/managerial.ts` passou a reexportar deste
 * módulo (ADR-0004).
 *
 * Invariantes deste módulo:
 *
 * 1. **Puro e sem I/O.** Nada aqui faz fetch, lê env, toca DOM ou banco. É por
 *    isso que a MESMA função decide matched/diverged no servidor e no que a
 *    tela exibe. Duas implementações dariam duas conciliações para o mesmo
 *    pagamento.
 * 2. **O vocabulário é o da persistência.** `RECONCILIATION_STATES`,
 *    `MANAGERIAL_ACCOUNT_KINDS` e os status são exatamente os `check (... in
 *    (...))` de `0035_financial_managerial.sql`, e um teste compara os dois
 *    lados. Se o SQL e o código divergirem, o teste falha.
 * 3. **A margem reusa `packages/pricing`.** O piso de margem aprovado é o
 *    `DEFAULT_PRICE_POLICY.minMarginPct` do Price Engine, e `belowFloor`/
 *    `needsHumanApproval` têm a MESMA semântica da recomendação de preço:
 *    margem abaixo do piso é alerta, e corrigir reprecificando exige aprovação
 *    humana — nunca auto-aplicada. Não reimplementamos política de margem aqui.
 * 4. **Gerencial, nunca fiscal.** Nada aqui emite nota, apura imposto ou move
 *    dinheiro. A conciliação LÊ o pagamento já registrado e aponta a
 *    divergência; o fiscal oficial permanece no Bling/contábil (D-16.3).
 */
import { DEFAULT_PRICE_POLICY } from "../../pricing/src/index.js";
/** Status da conferência de uma conciliação (espelho do `check` da 0035). */
export const RECONCILIATION_STATES = ["pending", "matched", "diverged"];
export const RECONCILIATION_STATE_LABELS = {
    pending: "Aguardando repasse",
    matched: "Concorda",
    diverged: "Diverge"
};
/** Tipos de conta gerencial (espelho do `check` da 0035). */
export const MANAGERIAL_ACCOUNT_KINDS = [
    "revenue",
    "payment_fee",
    "commission",
    "cogs",
    "expense",
    "tax_provision"
];
export const MANAGERIAL_ACCOUNT_KIND_LABELS = {
    revenue: "Receita",
    payment_fee: "Taxa de pagamento",
    commission: "Comissão",
    cogs: "Custo das mercadorias",
    expense: "Despesa",
    tax_provision: "Provisão de impostos"
};
/**
 * Sinal gerencial de cada conta: a receita ENTRA positiva; taxa, comissão,
 * custo, despesa e provisão SAEM negativas. É a convenção que faz a soma dos
 * lançamentos dar o resultado sem regra escondida na leitura (D-16.5).
 */
export const MANAGERIAL_ACCOUNT_SIGN = {
    revenue: 1,
    payment_fee: -1,
    commission: -1,
    cogs: -1,
    expense: -1,
    tax_provision: -1
};
export function isReconciliationState(value) {
    return typeof value === "string" && RECONCILIATION_STATES.includes(value);
}
export function isManagerialAccountKind(value) {
    return typeof value === "string" && MANAGERIAL_ACCOUNT_KINDS.includes(value);
}
/**
 * Aplica o sinal da conta ao valor BRUTO (sempre informado positivo). É o que
 * o servidor usa ao criar o lançamento e o que a tela usa para explicar por que
 * uma taxa aparece subtraindo.
 */
export function signedAmountForKind(kind, grossCents) {
    return MANAGERIAL_ACCOUNT_SIGN[kind] * Math.abs(grossCents);
}
/** Tolerância padrão (centavos) da conciliação: arredondamentos de repasse até
 * R$ 0,05 não são divergência de verdade. Acima disso, diverge. */
export const RECONCILIATION_TOLERANCE_CENTS = 5;
/**
 * Decide o status da conciliação a partir dos valores (D-16.2).
 *
 * - Sem repasse efetivo (`actualNetCents === null`): `pending` — não há o que
 *   conferir ainda, e `pending` não é "concorda".
 * - Com repasse: `matched` quando a diferença para o líquido esperado está
 *   dentro da tolerância; `diverged` caso contrário, com o motivo legível.
 *
 * O líquido esperado é o bruto menos as três deduções gerenciais (taxa,
 * comissão, custo) — a MESMA soma que os lançamentos materializam.
 */
export function reconcilePayment(input) {
    const tolerance = input.toleranceCents ?? RECONCILIATION_TOLERANCE_CENTS;
    const expectedNetCents = input.grossCents - input.paymentFeeCents - input.commissionCents - input.costCents;
    if (input.actualNetCents === null) {
        return {
            expectedNetCents,
            actualNetCents: null,
            status: "pending",
            divergenceReason: "",
            differenceCents: null
        };
    }
    const differenceCents = input.actualNetCents - expectedNetCents;
    if (Math.abs(differenceCents) <= tolerance) {
        return { expectedNetCents, actualNetCents: input.actualNetCents, status: "matched", divergenceReason: "", differenceCents };
    }
    const reason = differenceCents < 0
        ? `Repasse ${Math.abs(differenceCents)} centavos ABAIXO do líquido esperado (taxa/comissão/custo acima do previsto).`
        : `Repasse ${differenceCents} centavos ACIMA do líquido esperado (dedução menor que a prevista).`;
    return { expectedNetCents, actualNetCents: input.actualNetCents, status: "diverged", divergenceReason: reason, differenceCents };
}
/**
 * Piso de margem gerencial = o piso aprovado do Price Engine
 * (`packages/pricing`). Reusar a constante é o que impede o Financeiro de ter
 * uma política de margem própria divergente da precificação (D-16.1).
 */
export const MANAGERIAL_MIN_MARGIN_PCT = DEFAULT_PRICE_POLICY.minMarginPct;
/**
 * Margem REALIZADA (receita − taxa − comissão − custo), com a semântica de piso
 * e aprovação humana do Price Engine.
 *
 * `belowFloor`: a margem percentual ficou abaixo do piso aprovado — alerta de
 * gestão, não número neutro. `needsHumanApproval`: quando está abaixo do piso,
 * corrigir pela reprecificação é uma decisão que **exige aprovação humana**
 * (mesma regra de `computeRecommendedPrice`, que nunca auto-aplica). Margem sem
 * receita (ex.: produto sem venda registrada) tem percentual `null` — indefinido,
 * nunca 0.
 */
export function computeMargin(components) {
    const revenueCents = components.revenueCents;
    const paymentFeeCents = components.paymentFeeCents ?? 0;
    const commissionCents = components.commissionCents ?? 0;
    const costCents = components.costCents;
    const marginCents = revenueCents - paymentFeeCents - commissionCents - costCents;
    const marginPct = revenueCents > 0 ? Math.round((marginCents / revenueCents) * 10000) / 100 : null;
    const belowFloor = marginPct !== null && marginPct < MANAGERIAL_MIN_MARGIN_PCT;
    return {
        revenueCents,
        paymentFeeCents,
        commissionCents,
        costCents,
        marginCents,
        marginPct,
        belowFloor,
        // Abaixo do piso => a correção por reprecificação passa por aprovação
        // humana (nunca auto-aplicada), espelhando o Price Engine.
        needsHumanApproval: belowFloor
    };
}
